import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Input,
  Button,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { Pie, Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from "chart.js";
import {
  getAPIURL,
  getVendorID,
  getFormattedPrice,
} from "../../services/helper";
import http from "../../services/http";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const SalesByDepartment = () => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const [data, setData] = useState({ summary: {}, details: [] });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchSalesByDepartment();
  }, []);

  const fetchSalesByDepartment = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        vendor_id: vendorId,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      const { data } = await http.get(
        `${api_url}/reports/sales/categories?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching sales by department:", error);
    }
    setLoading(false);
  };

  const columns = [
    { name: "Category", selector: (row) => row.category_name, sortable: true },
    { name: "Total Sales", selector: (row) => row.total_sales, sortable: true },
    { name: "Total Amount", selector: (row) => getFormattedPrice(row.total_amount), sortable: true },
    { name: "Total Quantity", selector: (row) => row.total_quantity, sortable: true },
    { name: "Total Tax", selector: (row) => getFormattedPrice(row.total_tax), sortable: true },
    { name: "Total Discount", selector: (row) => getFormattedPrice(row.total_discount), sortable: true },
  ];

  const pieChartData = {
    labels: data.details.map(d => d.category_name),
    datasets: [{
      data: data.details.map(d => d.total_amount),
      backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
    }]
  };

  const barChartData = {
    labels: data.details.map(d => d.category_name),
    datasets: [{
      label: 'Total Amount',
      data: data.details.map(d => d.total_amount),
      backgroundColor: '#36A2EB',
    }]
  };

  return (
    <div className="page-body">
      <Container className="mt-4">
        <h2 className="text-center mb-4">Sales by Department</h2>

        {/* Date Range Filter */}
        <Card className="mb-4">
          <CardBody>
            <Row>
              <Col md={4}>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: e.target.value })
                  }
                />
              </Col>
              <Col md={4}>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: e.target.value })
                  }
                />
              </Col>
              <Col md={4}>
                <Button
                  color="primary"
                  onClick={fetchSalesByDepartment}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Fetch Report"}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Summary Cards */}
        {data.summary && (
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Sales</h5>
                  <h3>{data.summary.total_sales}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Amount</h5>
                  <h3>{getFormattedPrice(data.summary.total_amount)}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Quantity</h5>
                  <h3>{data.summary.total_quantity}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Tax</h5>
                  <h3>{getFormattedPrice(data.summary.total_tax)}</h3>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Charts */}
        {data.details.length > 0 && (
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <CardBody>
                  <CardTitle tag="h5">Department Distribution</CardTitle>
                  <Pie data={pieChartData} />
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <CardBody>
                  <CardTitle tag="h5">Department Sales Amount</CardTitle>
                  <Bar data={barChartData} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Data Table */}
        <Card>
          <CardBody>
            <CardTitle tag="h5">Department Details</CardTitle>
            <DataTable
              columns={columns}
              data={data.details}
              pagination
              striped
              highlightOnHover
              progressPending={loading}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default SalesByDepartment;